import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import Footer from '../Footer/Footer';
import FluidBackground from '../FluidBackground/FluidBackground';
import './ProductDetailPage.css';

// Sample product data (replace with your actual data fetching logic)
const sampleProduct = {
  id: 1,
  name: "Premium XR Experience",
  description: "An immersive XR experience with advanced interactive features and a sleek design. Perfect for event activations and brand showcasing that will captivate your audience and create memorable engagement.",
  techInclusions: [
    "Interactive elements",
    "Spatial tracking",
    "Multi-user capability",
    "High-resolution display",
    "Custom branding options",
    "Analytics dashboard"
  ],
  category: "XR Technology",
  tags: ["xr", "immersive", "events", "interactive", "branding"],
  images: [
    "https://bluedotdigitalagency.com/wp-content/uploads/2023/07/42-Immersive-experiences_-How-AR-and-VR-technologies-are-transforming-digital-marketing-2048x1152.png",
    "https://cdn.elearningindustry.com/wp-content/uploads/2023/07/Shutterstock_2256192757.jpg",
    "https://cdn.prod.website-files.com/6333febb37e1fbec46f57367/67236b44ce355b01df2f3a28_Entropy_for_Zsolnay_1-p-800.webp",
    "https://substackcdn.com/image/fetch/f_auto,q_auto:good,fl_progressive:steep/https%3A%2F%2Fsubstack-post-media.s3.amazonaws.com%2Fpublic%2Fimages%2F4e05141d-79f7-477f-9264-03dd39bb06d1_640x360.gif",
    "https://media0.giphy.com/media/v1.Y2lkPTc5MGI3NjExYnI0c2ltcTl4bmZvNmFjZ3VtdjN1Zm1hMG96enpkYW14eXNncnVmOCZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/U7JMDGUbBmfPBSILNB/giphy.gif"
  ]
};

const ProductDetailPage = () => {
  const { productId } = useParams();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // In a real app, you would fetch the product data based on productId
  const product = sampleProduct;
  
  // Create floating blobs for page decoration
  useEffect(() => {
    const pageDecoration = document.querySelector('.page-decoration');
    if (!pageDecoration) return;
    
    // Create a blob with specific vertical position and horizontal bias
    const createBlob = (verticalPosition, horizontalBias = null) => {
      const blob = document.createElement('div');
      blob.className = 'floating-blob';
      
      // Randomize horizontal position with optional bias
      let leftPosition;
      if (horizontalBias === 'far-left') {
        leftPosition = -5 + Math.random() * 15; // -5% to 10% (far left edge)
      } else if (horizontalBias === 'left') {
        leftPosition = 10 + Math.random() * 20; // 10% to 30% (left side)
      } else if (horizontalBias === 'center') {
        leftPosition = 30 + Math.random() * 40; // 30% to 70% (center)
      } else if (horizontalBias === 'right') {
        leftPosition = 70 + Math.random() * 20; // 70% to 90% (right side)
      } else if (horizontalBias === 'far-right') {
        leftPosition = 90 + Math.random() * 15; // 90% to 105% (far right edge)
      } else {
        leftPosition = 5 + Math.random() * 90; // 5% to 95% (full width)
      }
      
      blob.style.left = `${leftPosition}%`;
      
      // Set vertical position (negative to be above the viewport)
      blob.style.top = `${verticalPosition}px`;
      
      // Randomize size
      const size = 100 + Math.random() * 200; // Random size between 100px and 300px
      blob.style.width = `${size}px`;
      blob.style.height = `${size}px`;
      
      // Randomize opacity and color intensity
      const opacity = 0.25 + Math.random() * 0.15; // Between 0.25 and 0.4
      blob.style.background = `rgba(227, 41, 97, ${opacity})`;
      
      // Set parallax speed
      blob.dataset.speed = 0.2 + Math.random() * 0.2; // Random speed between 0.2 and 0.4
      
      return blob;
    };
    
    // Create blobs with better distribution
    const totalBlobs = 25; // Fewer blobs for product page
    const blobs = [];
    
    // Position blobs from -5000px up to the top of the viewport
    for (let i = 0; i < totalBlobs; i++) {
      const verticalPosition = -5000 + (i * (5000 / totalBlobs));
      
      // Determine horizontal bias based on position
      let horizontalBias;
      const position = i % 5;
      if (position === 0) horizontalBias = 'far-left';
      else if (position === 1) horizontalBias = 'left';
      else if (position === 2) horizontalBias = 'center';
      else if (position === 3) horizontalBias = 'right';
      else horizontalBias = 'far-right';
      
      const blob = createBlob(verticalPosition, horizontalBias);
      pageDecoration.appendChild(blob);
      blobs.push(blob);
    }
    
    // Handle scroll to move blobs with parallax effect
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      
      // Update all blobs position
      blobs.forEach(blob => {
        const speed = parseFloat(blob.dataset.speed);
        const initialTop = parseFloat(blob.style.top.replace('px', ''));
        
        blob.style.transform = `translateY(${currentScrollY * speed}px)`;
        
        // Calculate actual position
        const blobTop = initialTop + (currentScrollY * speed);
        
        // Make blobs visible only when they're near or in the viewport
        if (blobTop < -viewportHeight || blobTop > viewportHeight * 2) {
          blob.style.opacity = '0';
        } else {
          // Fade in as they approach the viewport
          const distance = Math.abs(blobTop);
          const maxDistance = viewportHeight;
          // Opacity range: minimum 0.2, maximum 0.5
          const opacity = 0.2 + (0.3 * (1 - Math.min(distance / maxDistance, 1)));
          blob.style.opacity = opacity.toString();
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    
    // Call once to set initial positions
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      // Clean up blobs
      blobs.forEach(blob => {
        if (pageDecoration.contains(blob)) {
          pageDecoration.removeChild(blob);
        }
      });
    };
  }, []);
  
  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === product.images.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? product.images.length - 1 : prevIndex - 1
    );
  };
  
  const selectImage = (index) => {
    setCurrentImageIndex(index);
  };
  
  return (
    <div className="product-detail-container">
      {/* Fluid background component */}
      <FluidBackground />
      
      {/* Container for dynamically created blobs */}
      <div className="page-decoration"></div>
      
      <div className="product-detail-content">
        <div className="product-info-section">
          <h1 className="product-title" style={{ color: '#E32961' }}>{product.name}</h1>
          
          <div className="info-section">
            <h2 style={{ color: '#E32961' }}>Description</h2>
            <p>{product.description}</p>
          </div>
          
          <div className="info-section">
            <h2 style={{ color: '#E32961' }}>Tags</h2>
            <div className="tags-container">
              {product.tags.map((tag, index) => (
                <span key={index} className="tag">{tag}</span>
              ))}
            </div>
          </div>
          
          <button className="buy-button">Request Demo</button>
        </div>
        
        <div className="product-gallery-section">
          <div className="main-image-container">
            <button className="gallery-nav prev" onClick={prevImage}>
              <FaArrowLeft />
            </button>
            <img 
              src={product.images[currentImageIndex]} 
              alt={`${product.name} - view ${currentImageIndex + 1}`} 
              className="main-image"
            />
            <button className="gallery-nav next" onClick={nextImage}>
              <FaArrowRight />
            </button>
          </div>
          
          <div className="thumbnail-container">
            {product.images.map((image, index) => (
              <div 
                key={index} 
                className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                onClick={() => selectImage(index)}
              >
                <img src={image} alt={`Thumbnail ${index + 1}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <Footer />
      
      <style>
        {`
          /* Page-wide blob styling */
          .page-decoration {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 1;
            overflow: hidden;
          }
          
          .floating-blob {
            position: absolute;
            border-radius: 50%;
            filter: blur(15px);
            box-shadow: 0 0 25px rgba(227, 41, 97, 0.5);
            will-change: transform, opacity;
            transition: opacity 0.5s ease;
          }
          
          /* Fix horizontal scrolling issue */
          .product-detail-container {
            width: 100%;
            max-width: 100vw;
            overflow-x: hidden;
            box-sizing: border-box;
          }
          
          /* Make product showcasing background transparent */
          .product-detail-content {
            background: transparent;
            width: 95%;
            max-width: 1800px;
            margin: 0 auto;
            padding: 1rem;
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: space-between;
            margin-top: 80px;
            box-sizing: border-box;
          }
          
          .product-info-section, .product-gallery-section {
            background: transparent;
            color: white;
            width: 48%;
            box-sizing: border-box;
          }
          
          @media (max-width: 992px) {
            .product-info-section, .product-gallery-section {
              width: 100%;
            }
            
            .product-gallery-section {
              margin-top: 2rem;
            }
          }
          
          .main-image-container, .thumbnail-container {
            background: transparent;
            width: 100%;
            box-sizing: border-box;
          }
          
          .main-image {
            width: 100%;
            height: auto;
            border-radius: 12px;
            object-fit: cover;
          }
          
          .thumbnail {
            border: 2px solid transparent;
            border-radius: 8px;
            overflow: hidden;
            cursor: pointer;
            transition: all 0.3s ease;
            flex: 0 0 auto;
            width: 80px;
            height: 60px;
          }
          
          .thumbnail.active {
            border-color: #E32961;
            box-shadow: 0 0 10px rgba(227, 41, 97, 0.7);
          }
          
          .thumbnail img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          
          .thumbnail-container {
            display: flex;
            gap: 10px;
            margin-top: 15px;
            overflow-x: auto;
            padding-bottom: 10px;
          }
          
          .info-section {
            margin-bottom: 1.5rem;
          }
          
          .info-section p, .info-section li {
            color: white;
            line-height: 1.6;
          }
          
          .tech-list {
            padding-left: 20px;
            list-style-type: none;
          }
          
          .tech-list li {
            position: relative;
            padding-left: 15px;
            margin-bottom: 8px;
          }
          
          .tech-list li:before {
            content: "";
            position: absolute;
            left: 0;
            top: 50%;
            width: 8px;
            height: 2px;
            background-color: #E32961;
            transform: translateY(-50%);
          }
          
          .tags-container {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
          }
          
          .tag {
            background: rgba(227, 41, 97, 0.2);
            border: 1px solid rgba(227, 41, 97, 0.5);
            color: white;
            padding: 5px 12px;
            border-radius: 20px;
            font-size: 0.9rem;
          }
          
          .buy-button {
            background: #E32961;
            color: white;
            border: none;
            padding: 12px 25px;
            border-radius: 8px;
            font-size: 1.1rem;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-top: 1rem;
          }
          
          .buy-button:hover {
            background: #c71e50;
            box-shadow: 0 0 15px rgba(227, 41, 97, 0.7);
          }
          
          .gallery-nav {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            background: rgba(0, 0, 0, 0.5);
            color: white;
            border: none;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.3s ease;
            z-index: 2;
          }
          
          .gallery-nav:hover {
            background: rgba(227, 41, 97, 0.8);
          }
          
          .gallery-nav.prev {
            left: 10px;
          }
          
          .gallery-nav.next {
            right: 10px;
          }
        `}
      </style>
    </div>
  );
};

export default ProductDetailPage; 